import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';

import { bezierHandles, bounds2, nearestSegment, previewCurve, snapPoint, type Vec2 } from './curve-math.js';

/**
 * Interactive 2D canvas (SVG) for a list of control points: click empty space
 * to add a point, click the curve to insert one, drag to move (Shift locks
 * to one axis, Ctrl inverts snapping), double-click or Delete removes, wheel
 * zooms, middle/right drag pans, F frames. Draws the curve exactly as prism
 * builds it (centripetal Catmull-Rom when `smooth`) plus read-only Bezier
 * handles of the selected point.
 *
 * Point changes during a drag go to `onMove` (the owner keeps a draft) and
 * the gesture ends with `onCommit`, so a whole drag is one undo step.
 */
export interface CurveCanvasProps {
  points: readonly Vec2[];
  closed?: boolean;
  smooth?: boolean;
  samples?: number;
  selected: number | null;
  onSelect: (index: number | null) => void;
  onMove: (index: number, p: Vec2) => void;
  onCommit: () => void;
  /** Click on empty space: add a point, returning its index (null = not added). */
  onAdd?: (p: Vec2) => number | null;
  /** Click on the curve between `segment` and `segment + 1` (at parameter `t`). */
  onInsert?: (segment: number, p: Vec2, t: number) => number | null;
  onDelete?: (index: number) => void;
  grid: number;
  snap: boolean;
  /** Points can't go left of this x (lathe radius ≥ 0). */
  minX?: number;
  /** Draw the curve mirrored across x = 0 (lathe silhouette). */
  mirrorX?: boolean;
  /** +y points down the screen (cloth: distance from the attached edge). */
  flipY?: boolean;
  axes: [string, string];
  /** Non-editable reference outlines. */
  ghosts?: readonly { points: readonly Vec2[]; closed?: boolean }[];
  /** Draw these point pairs instead of a curve (skeletons). */
  edges?: readonly (readonly [number, number])[];
  /** Circle around each point (world units): radii, thickness. */
  radii?: readonly (number | undefined)[];
  height?: number;
  /** Changing this re-frames the view (e.g. switching projections). */
  frameKey?: string;
}

interface View {
  cx: number;
  cy: number;
  zoom: number;
}

type Gesture =
  | { kind: 'drag'; index: number; start: Vec2; grab: Vec2; changed: boolean; pointerId: number }
  | { kind: 'pan'; sx: number; sy: number; view: View; pointerId: number };

function fitView(points: readonly Vec2[], ghosts: CurveCanvasProps['ghosts'], w: number, h: number): View {
  const all: Vec2[] = [[0, 0], ...points, ...(ghosts ?? []).flatMap((g) => g.points)];
  const b = bounds2(all) ?? [-1, -1, 1, 1];
  const bw = Math.max(b[2] - b[0], 0.05);
  const bh = Math.max(b[3] - b[1], 0.05);
  const zoom = Math.max(1, Math.min((w - 48) / bw, (h - 48) / bh));
  return { cx: (b[0] + b[2]) / 2, cy: (b[1] + b[3]) / 2, zoom };
}

export function CurveCanvas(props: CurveCanvasProps) {
  const {
    points,
    closed = false,
    smooth = false,
    samples = 6,
    selected,
    grid,
    snap,
    minX,
    flipY = false,
    axes,
    height = 220,
  } = props;
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [width, setWidth] = useState(280);
  const [view, setView] = useState<View>(() => fitView(points, props.ghosts, 280, height));
  const [cursor, setCursor] = useState<Vec2 | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  const ys = flipY ? -1 : 1;
  const sx = (x: number) => width / 2 + (x - view.cx) * view.zoom;
  const sy = (y: number) => height / 2 - ys * (y - view.cy) * view.zoom;
  const pt = (p: readonly number[]) => `${sx(p[0] ?? 0).toFixed(1)},${sy(p[1] ?? 0).toFixed(1)}`;

  const fit = () => setView(fitView(points, props.ghosts, width, height));

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(Math.max(120, el.clientWidth)));
    ro.observe(el);
    setWidth(Math.max(120, el.clientWidth));
    // Wheel zoom around the cursor (a native listener: React's is passive).
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const v = viewRef.current;
      const px = e.clientX - r.left;
      const py = e.clientY - r.top;
      const wx = v.cx + (px - r.width / 2) / v.zoom;
      const wy = v.cy + (ys * (r.height / 2 - py)) / v.zoom;
      const zoom = Math.min(1e5, Math.max(0.5, v.zoom * Math.pow(1.0015, -e.deltaY)));
      setView({ cx: wx - (wx - v.cx) * (v.zoom / zoom), cy: wy - (wy - v.cy) * (v.zoom / zoom), zoom });
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      ro.disconnect();
      el.removeEventListener('wheel', onWheel);
    };
  }, [ys]);

  // Re-frame on request (and once the real width is known).
  const framed = useRef<string | undefined>(undefined);
  useEffect(() => {
    const key = `${props.frameKey ?? ''}|${width}`;
    if (framed.current === key) return;
    framed.current = key;
    setView(fitView(points, props.ghosts, width, height));
  }, [props.frameKey, width]);

  function toWorld(e: { clientX: number; clientY: number }): Vec2 {
    const r = svgRef.current?.getBoundingClientRect();
    const px = e.clientX - (r?.left ?? 0);
    const py = e.clientY - (r?.top ?? 0);
    return [view.cx + (px - width / 2) / view.zoom, view.cy + (ys * (height / 2 - py)) / view.zoom];
  }

  function constrain(p: Vec2, e: { ctrlKey: boolean }): Vec2 {
    let q: Vec2 = [p[0], p[1]];
    if (snap !== e.ctrlKey && grid > 0) q = snapPoint(q, grid);
    if (minX !== undefined && q[0] < minX) q[0] = minX;
    return q;
  }

  function startDrag(e: ReactPointerEvent, index: number, start: Vec2, changed: boolean) {
    svgRef.current?.setPointerCapture(e.pointerId);
    gesture.current = { kind: 'drag', index, start, grab: toWorld(e), changed, pointerId: e.pointerId };
  }

  function onPointerDown(e: ReactPointerEvent<SVGSVGElement>) {
    svgRef.current?.focus();
    if (e.button === 1 || e.button === 2 || (e.button === 0 && e.altKey)) {
      e.preventDefault();
      svgRef.current?.setPointerCapture(e.pointerId);
      gesture.current = { kind: 'pan', sx: e.clientX, sy: e.clientY, view, pointerId: e.pointerId };
      return;
    }
    if (e.button !== 0) return;
    const p = toWorld(e);
    const q = constrain(p, e);
    if (props.onInsert && !props.edges && points.length >= 2) {
      const per = smooth && points.length >= 3 ? Math.min(64, Math.max(1, Math.floor(samples))) : 1;
      const curve = previewCurve(points, closed, smooth, samples);
      const hit = nearestSegment(curve, p, closed && points.length >= 3, 7 / view.zoom);
      if (hit) {
        const segment = Math.floor(hit.segment / per);
        const t = (hit.segment - segment * per + hit.t) / per;
        const index = props.onInsert(segment, q, t);
        if (index !== null) {
          props.onSelect(index);
          startDrag(e, index, q, true);
        }
        return;
      }
    }
    if (props.onAdd) {
      const index = props.onAdd(q);
      if (index !== null) {
        props.onSelect(index);
        startDrag(e, index, q, true);
        return;
      }
    }
    props.onSelect(null);
  }

  function onPointDown(e: ReactPointerEvent, index: number) {
    if (e.button !== 0 || e.altKey) return;
    e.stopPropagation();
    svgRef.current?.focus();
    props.onSelect(index);
    const p = points[index];
    if (p) startDrag(e, index, [p[0], p[1]], false);
  }

  function onPointerMove(e: ReactPointerEvent<SVGSVGElement>) {
    const g = gesture.current;
    const p = toWorld(e);
    setCursor(p);
    if (!g) return;
    if (g.kind === 'pan') {
      setView({ ...g.view, cx: g.view.cx - (e.clientX - g.sx) / g.view.zoom, cy: g.view.cy + (ys * (e.clientY - g.sy)) / g.view.zoom });
      return;
    }
    let dx = p[0] - g.grab[0];
    let dy = p[1] - g.grab[1];
    if (!g.changed && Math.hypot(dx, dy) * view.zoom < 2) return;
    if (e.shiftKey) {
      if (Math.abs(dx) > Math.abs(dy)) dy = 0;
      else dx = 0;
    }
    g.changed = true;
    props.onMove(g.index, constrain([g.start[0] + dx, g.start[1] + dy], e));
  }

  function onPointerUp(e: ReactPointerEvent<SVGSVGElement>) {
    const g = gesture.current;
    gesture.current = null;
    if (svgRef.current?.hasPointerCapture(e.pointerId)) svgRef.current.releasePointerCapture(e.pointerId);
    if (g?.kind === 'drag' && g.changed) props.onCommit();
  }

  function onKeyDown(e: ReactKeyboardEvent) {
    if ((e.key === 'Delete' || e.key === 'Backspace') && selected !== null && props.onDelete) {
      e.preventDefault();
      e.stopPropagation();
      props.onDelete(selected);
    } else if (e.key === 'Escape') {
      props.onSelect(null);
    } else if (e.key === 'f' || e.key === 'F' || e.key === 'Home') {
      e.preventDefault();
      fit();
    } else if (e.key.startsWith('Arrow') && selected !== null) {
      const p = points[selected];
      if (!p) return;
      e.preventDefault();
      e.stopPropagation();
      const step = (grid > 0 ? grid : 0.01) * (e.shiftKey ? 10 : 1);
      const d: Vec2 =
        e.key === 'ArrowLeft' ? [-step, 0] : e.key === 'ArrowRight' ? [step, 0] : e.key === 'ArrowUp' ? [0, ys * step] : [0, -ys * step];
      let q: Vec2 = [p[0] + d[0], p[1] + d[1]];
      if (minX !== undefined) q[0] = Math.max(minX, q[0]);
      q = snapPoint(q, 0);
      props.onMove(selected, q);
      props.onCommit();
    }
  }

  // ---- drawing
  const curve = props.edges ? [] : previewCurve(points, closed, smooth, samples);
  const curveClosed = closed && points.length >= 3;
  const poly = (list: readonly (readonly number[])[], close: boolean) => `${list.map(pt).join(' ')}${close && list[0] ? ` ${pt(list[0])}` : ''}`;

  const gridLines: { d: string; major: boolean }[] = [];
  {
    let step = grid > 0 ? grid : 0.1;
    while (step * view.zoom < 9) step *= 2;
    while (step * view.zoom > 80) step /= 2;
    const x0 = view.cx - width / 2 / view.zoom;
    const x1 = view.cx + width / 2 / view.zoom;
    const y0 = view.cy - height / 2 / view.zoom;
    const y1 = view.cy + height / 2 / view.zoom;
    for (let i = Math.ceil(x0 / step); i * step <= x1 && gridLines.length < 300; i++) {
      gridLines.push({ d: `M${sx(i * step).toFixed(1)},0V${height}`, major: i % 5 === 0 });
    }
    for (let i = Math.ceil(y0 / step); i * step <= y1 && gridLines.length < 600; i++) {
      gridLines.push({ d: `M0,${sy(i * step).toFixed(1)}H${width}`, major: i % 5 === 0 });
    }
  }
  const handles = smooth && !props.edges && selected !== null && points.length >= 3 ? bezierHandles(points, curveClosed)[selected] : undefined;
  const sel = selected !== null ? points[selected] : undefined;

  return (
    <div className="curve-canvas" style={{ height }}>
      <svg
        ref={svgRef}
        className="curve-svg"
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        tabIndex={0}
        role="application"
        aria-label={`Curve editor (${axes[0]} / ${axes[1]})`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => setCursor(null)}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={onKeyDown}
      >
        <rect className="curve-bg" x={0} y={0} width={width} height={height} />
        {gridLines.map((l, i) => (
          <path key={i} className={l.major ? 'curve-grid is-major' : 'curve-grid'} d={l.d} />
        ))}
        {minX !== undefined && sx(minX) > 0 && <rect className="curve-forbidden" x={0} y={0} width={Math.max(0, sx(minX))} height={height} />}
        <path className="curve-axis axis-u" d={`M0,${sy(0).toFixed(1)}H${width}`} />
        <path className="curve-axis axis-v" d={`M${sx(0).toFixed(1)},0V${height}`} />
        {(props.ghosts ?? []).map((g, i) => (
          <polyline key={i} className="curve-ghost" points={poly(g.points, g.closed ?? true)} />
        ))}
        {props.mirrorX && curve.length >= 2 && (
          <polyline className="curve-mirror" points={poly(curve.map((p) => [-p[0], p[1]]), curveClosed)} />
        )}
        {props.edges?.map(([a, b], i) => {
          const pa = points[a];
          const pb = points[b];
          return pa && pb ? <polyline key={i} className="curve-line" points={poly([pa, pb], false)} /> : null;
        })}
        {smooth && points.length >= 3 && !props.edges && <polyline className="curve-control" points={poly(points, curveClosed)} />}
        {curve.length >= 2 && <polyline className="curve-line" points={poly(curve, curveClosed)} />}
        {props.radii?.map((r, i) => {
          const p = points[i];
          return p && r && r > 0 ? <circle key={i} className="curve-radius" cx={sx(p[0])} cy={sy(p[1])} r={r * view.zoom} /> : null;
        })}
        {handles && sel && (
          <g className="curve-handles">
            <polyline points={poly([handles[0], sel, handles[1]], false)} />
            {handles.map((h, i) => (
              <rect key={i} x={sx(h[0] ?? 0) - 2.5} y={sy(h[1] ?? 0) - 2.5} width={5} height={5} />
            ))}
          </g>
        )}
        {points.map((p, i) => (
          <circle
            key={i}
            className={`curve-point${i === selected ? ' is-selected' : ''}${i === 0 && !props.edges ? ' is-first' : ''}`}
            cx={sx(p[0])}
            cy={sy(p[1])}
            r={i === selected ? 5.5 : 4.5}
            onPointerDown={(e) => onPointDown(e, i)}
            onDoubleClick={(e) => {
              e.stopPropagation();
              props.onDelete?.(i);
            }}
          >
            <title>{`#${i}  ${axes[0]} ${p[0].toFixed(3)}  ${axes[1]} ${p[1].toFixed(3)}`}</title>
          </circle>
        ))}
        <text className="curve-axis-label" x={width - 6} y={sy(0) - 4 < 12 ? 12 : Math.min(height - 4, sy(0) - 4)} textAnchor="end">
          {axes[0]}
        </text>
        <text className="curve-axis-label" x={Math.min(width - 14, Math.max(4, sx(0) + 4))} y={flipY ? height - 6 : 12}>
          {axes[1]}
        </text>
        {cursor && (
          <text className="curve-readout" x={6} y={height - 6}>
            {`${axes[0]} ${cursor[0].toFixed(3)}  ${axes[1]} ${cursor[1].toFixed(3)}`}
          </text>
        )}
      </svg>
    </div>
  );
}
